import * as R from 'remeda';
import { HIERARCHY_SEPARATOR } from '@/shared/lib/format/separators';
import type { CascaderOption, ShowCheckedStrategy } from '../helpers/types';
import { getAllDescendantPaths, getSelectedOptions, isPathSelected } from './path-utils';

const joinKey = (path: string[]) => path.join('|');

/**
 * 選択パスを統合して表示用パスを取得する（共通ロジック）
 *
 * 全子孫が選択されている親ノードは統合して親パスのみ表示する。
 * 統合されなかった部分はリーフノードのみ表示する。
 */
const consolidateDisplayPaths = (
  selectedPaths: string[][],
  options: CascaderOption[],
): string[][] => {
  if (selectedPaths.length === 0) return [];

  const displayPaths: string[][] = [];
  const coveredKeys = new Set<string>();

  // パスの長さでソート（短い順 = 親優先）
  const sortedPaths = R.sortBy(selectedPaths, (p) => p.length);

  for (const path of sortedPaths) {
    const key = joinKey(path);
    if (coveredKeys.has(key)) continue;

    const lastOpt = R.last(getSelectedOptions(path, options));
    if (!lastOpt) continue;

    if (lastOpt.children && lastOpt.children.length > 0) {
      // 親ノード: 全子孫が選択されている場合のみ表示
      const allDescendants = getAllDescendantPaths(path.slice(0, -1), lastOpt);
      const allSelected = allDescendants.every((dp) => isPathSelected(dp, selectedPaths));

      if (allSelected) {
        displayPaths.push(path);
        for (const dp of allDescendants) {
          coveredKeys.add(joinKey(dp));
        }
      }
      // 全子孫未選択の親はスキップ（子ノードが自身をハンドル）
    } else {
      // リーフノード
      displayPaths.push(path);
      coveredKeys.add(key);
    }
  }

  return displayPaths;
};

/**
 * 表示用のパスを取得（showCheckedStrategy に基づく）
 *
 * 両戦略とも、全子孫が選択されている親ノードは統合して親パスのみ表示する。
 * - SHOW_CHILD: 統合されなかった部分はリーフノードのみ表示
 * - SHOW_PARENT: 統合されなかった部分もリーフノードのみ表示（統合ロジックは共通）
 */
export const getDisplayPaths = (
  selectedPaths: string[][],
  options: CascaderOption[],
  _showCheckedStrategy: ShowCheckedStrategy,
): string[][] => {
  return consolidateDisplayPaths(selectedPaths, options);
};

/**
 * タグ表示用のラベルを生成（パスの各ノードラベルを区切り文字で結合）
 */
export const getTagLabel = (path: string[], options: CascaderOption[]): string => {
  const selectedOptions = getSelectedOptions(path, options);
  return selectedOptions.map((o) => o.label).join(HIERARCHY_SEPARATOR);
};

/**
 * 表示テキストを生成
 */
export const getDisplayText = (
  activePath: string[],
  options: CascaderOption[],
  displayRender?: (labels: string[]) => string,
): string => {
  if (activePath.length === 0) return '';

  const selectedOptions = getSelectedOptions(activePath, options);
  const labels = selectedOptions.map((opt) => opt.label);

  if (displayRender) {
    return displayRender(labels);
  }

  return labels.join(HIERARCHY_SEPARATOR);
};
